// Commissions → Bonuses: Account Manager revenue-bonus progress (month,
// quarter, year — the highest tier reached in each, and they stack), award
// creation for finished periods, approval, and the Concierge development
// bonus that management sets each quarter.
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { BONUS_KIND_LABEL, type BonusTier, errReason, fmtDay, money, nameOr, periodKeyLabel, usePlans } from "@/lib/compPlans";
import { Button } from "@/components/ui/Button";
import type { GoToTab } from "./CommissionsPlansPage";
import { ConfirmAction, Empty, Field, INPUT, Loading, Notice, Pill, Section, TEXTAREA, T_HEAD, T_NUM, T_ROW, T_TD, T_TH, T_WRAP } from "./parts";

interface BonusPeriod {
  kind: "monthly" | "quarterly" | "annual";
  key: string;
  revenue: string | null;
  reached: BonusTier | null;
  next: BonusTier | null;
}

interface BonusAward {
  id: string;
  rep_id: string;
  rep_name: string | null;
  kind: string;
  period_key: string;
  revenue: string | null;
  tier_threshold: string | null;
  amount: string;
  status: "pending" | "approved" | "rejected" | string;
  note: string | null;
  payout_batch_id: string | null;
  decided_at: string | null;
  batch_status: string | null;
}

interface BonusesResponse {
  basis: "invoice" | "payment" | "closure" | "paid_and_billed" | null;
  reps: Array<{ repId: string; repName: string; periods: BonusPeriod[] }>;
  awards: BonusAward[];
}

const BASIS_LABEL: Record<string, string> = {
  invoice: "by invoice date",
  payment: "when paid in full",
  closure: "when the order closes",
  paid_and_billed: "when the invoice is paid and every PO has a bill",
};

const PERIOD_LABEL: Record<string, string> = { monthly: "This month", quarterly: "This quarter", annual: "This year" };

function quarterOptions(): string[] {
  const now = new Date();
  const out: string[] = [];
  let y = now.getFullYear();
  let q = Math.floor(now.getMonth() / 3) + 1;
  for (let i = 0; i < 4; i++) {
    out.push(`${y}-Q${q}`);
    q -= 1;
    if (q === 0) {
      q = 4;
      y -= 1;
    }
  }
  return out;
}

export function BonusesTab({ go }: { go: GoToTab }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["commissions", "bonuses"], queryFn: () => api<BonusesResponse>("/commissions/bonuses") });
  const [msg, setMsg] = useState<{ tone: "ok" | "risk"; text: string } | null>(null);

  const generate = useMutation({
    mutationFn: () => api<{ created: number; updated: number }>("/commissions/bonuses/generate", { method: "POST" }),
    onSuccess: (r) => {
      setMsg({
        tone: "ok",
        text:
          r.created + r.updated === 0
            ? "Checked every finished period — no new awards."
            : `${r.created} new ${r.created === 1 ? "award" : "awards"} waiting for approval${r.updated ? `, ${r.updated} updated` : ""}.`,
      });
      void qc.invalidateQueries({ queryKey: ["commissions"] });
    },
    onError: (e) => setMsg({ tone: "risk", text: errReason(e) }),
  });

  const decide = useMutation({
    mutationFn: (a: { id: string; approve: boolean; reason?: string }) =>
      api(`/commissions/bonuses/${a.id}/decide`, { method: "POST", body: JSON.stringify({ approve: a.approve, reason: a.reason ?? null }) }),
    onSuccess: (_r, a) => {
      setMsg({ tone: "ok", text: a.approve ? "Bonus approved — it goes into the next payout batch." : "Bonus rejected." });
      void qc.invalidateQueries({ queryKey: ["commissions"] });
    },
    onError: (e) => setMsg({ tone: "risk", text: errReason(e) }),
  });

  if (q.isError) return <Notice tone="risk">{errReason(q.error)}</Notice>;
  if (!q.data) return <Loading />;
  const d = q.data;

  return (
    <div>
      {msg && (
        <Notice tone={msg.tone} className="mb-5">
          {msg.text}
        </Notice>
      )}

      {!d.basis && (
        <Notice tone="warn" className="mb-8">
          Finance must set when revenue counts toward bonuses (invoice date, paid in full, or order closed) before progress shows and
          awards can be created.{" "}
          <button type="button" className="font-semibold underline" onClick={() => go("settings")}>
            Set it in Settings
          </button>
        </Notice>
      )}

      <Section
        title="Account Manager revenue bonuses"
        intro={
          <>
            Each period earns the highest tier reached — never every tier crossed. Monthly, quarterly and annual bonuses stack.
            {d.basis ? ` Revenue counts ${BASIS_LABEL[d.basis]}.` : ""}
          </>
        }
        action={
          <Button onClick={() => generate.mutate()} disabled={!d.basis || generate.isPending}>
            {generate.isPending ? "Checking…" : "Create awards for finished periods"}
          </Button>
        }
      >
        {d.reps.length === 0 ? (
          <Empty>No reps are on the Account Manager plan.</Empty>
        ) : (
          <div className={T_WRAP}>
            <table className="w-full min-w-[1000px]">
              <thead className={T_HEAD}>
                <tr>
                  <th className={T_TH}>Account Manager</th>
                  <th className={T_TH}>This month</th>
                  <th className={T_TH}>This quarter</th>
                  <th className={T_TH}>This year</th>
                </tr>
              </thead>
              <tbody>
                {d.reps.map((r) => (
                  <tr key={r.repId} className={T_ROW}>
                    <td className={`${T_TD} font-medium`}>{r.repName}</td>
                    {(["monthly", "quarterly", "annual"] as const).map((k) => {
                      const p = r.periods.find((x) => x.kind === k);
                      return (
                        <td key={k} className={T_TD}>
                          {!p || p.revenue == null ? (
                            <span className="text-muted">—</span>
                          ) : (
                            <ProgressCell p={p} />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Awards" intro="Awards wait for approval. Approved awards go into the next payout batch.">
        {d.awards.length === 0 ? (
          <Empty>No bonus awards yet.</Empty>
        ) : (
          <div className={T_WRAP}>
            <table className="w-full min-w-[1100px]">
              <thead className={T_HEAD}>
                <tr>
                  <th className={T_TH}>Rep</th>
                  <th className={T_TH}>Bonus</th>
                  <th className={T_TH}>Period</th>
                  <th className={`${T_TH} text-right`}>Revenue</th>
                  <th className={`${T_TH} text-right`}>Tier</th>
                  <th className={`${T_TH} text-right`}>Amount</th>
                  <th className={T_TH}>Status</th>
                  <th className={T_TH} />
                </tr>
              </thead>
              <tbody>
                {d.awards.map((a) => (
                  <tr key={a.id} className={T_ROW}>
                    <td className={T_TD}>{nameOr(a.rep_name)}</td>
                    <td className={T_TD}>
                      {BONUS_KIND_LABEL[a.kind] ?? a.kind}
                      {a.note ? <span className="block text-[14px] text-muted">{a.note}</span> : null}
                    </td>
                    <td className={T_TD}>{periodKeyLabel(a.period_key)}</td>
                    <td className={T_NUM}>{a.revenue ? money(a.revenue) : "—"}</td>
                    <td className={T_NUM}>{a.tier_threshold ? money(a.tier_threshold) : "—"}</td>
                    <td className={`${T_NUM} font-semibold`}>{money(a.amount)}</td>
                    <td className={T_TD}>
                      <AwardStatus a={a} />
                    </td>
                    <td className={`${T_TD} whitespace-nowrap text-right`}>
                      {a.status === "pending" && (
                        <div className="flex flex-wrap justify-end gap-2">
                          <ConfirmAction
                            label="Approve"
                            confirmLabel={`Yes, approve ${money(a.amount)}`}
                            disabled={decide.isPending}
                            onConfirm={() => decide.mutate({ id: a.id, approve: true })}
                          />
                          <ConfirmAction
                            label="Reject"
                            variant="outline"
                            confirmLabel="Yes, reject"
                            needReason="Short reason (kept in the history)"
                            disabled={decide.isPending}
                            onConfirm={(reason) => decide.mutate({ id: a.id, approve: false, reason })}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <DevelopmentBonusForm onDone={(t) => setMsg({ tone: "ok", text: t })} onError={(t) => setMsg({ tone: "risk", text: t })} />
    </div>
  );
}

function ProgressCell({ p }: { p: BonusPeriod }) {
  const rev = Number(p.revenue ?? 0);
  return (
    <div>
      <div className="text-[16px] font-medium tabular-nums">{money(p.revenue)}</div>
      <div className="mt-0.5 text-[14px] text-muted">{periodKeyLabel(p.key)}</div>
      <div className="mt-1.5 text-[14px]">
        {p.reached ? (
          <span className="text-ok">
            Tier {money(p.reached.threshold)} reached → {money(p.reached.amount)}
          </span>
        ) : (
          <span className="text-muted">No tier yet</span>
        )}
      </div>
      {p.next && (
        <div className="text-[14px] text-muted">
          Next: {money(p.next.threshold)} ({money(p.next.amount)}) — {money(Math.max(0, p.next.threshold - rev))} to go
        </div>
      )}
    </div>
  );
}

function AwardStatus({ a }: { a: BonusAward }) {
  if (a.status === "pending") return <Pill tone="warn">Waiting for approval</Pill>;
  if (a.status === "rejected") return <Pill tone="closed">Rejected</Pill>;
  if (a.batch_status === "paid") return <Pill tone="active">Paid</Pill>;
  if (a.payout_batch_id) return <Pill tone="active">Approved · in a batch</Pill>;
  return <Pill tone="active">Approved{a.decided_at ? ` ${fmtDay(a.decided_at)}` : ""}</Pill>;
}

function DevelopmentBonusForm({ onDone, onError }: { onDone: (t: string) => void; onError: (t: string) => void }) {
  const qc = useQueryClient();
  const plans = usePlans();
  const quarters = useMemo(quarterOptions, []);
  const [repId, setRepId] = useState("");
  const [quarter, setQuarter] = useState(quarters[0]!);
  const [amount, setAmount] = useState(""); // never prefilled at the cap
  const [note, setNote] = useState("");

  const conciergePlan = plans.data?.plans.find((p) => p.key === "concierge");
  const conciergeReps = (plans.data?.reps ?? []).filter((r) =>
    conciergePlan ? r.current_plan === conciergePlan.name : /concierge/i.test(r.current_plan ?? ""),
  );
  const latest = conciergePlan
    ? (plans.data?.versions ?? []).filter((v) => v.plan_id === conciergePlan.id).sort((a, b) => b.version - a.version)[0]
    : undefined;
  const cap = latest?.development_bonus_annual_cap;

  const create = useMutation({
    mutationFn: () =>
      api("/commissions/bonuses/development", {
        method: "POST",
        body: JSON.stringify({ rep_id: repId, quarter, amount: Number(amount).toFixed(2), note: note.trim() }),
      }),
    onSuccess: () => {
      onDone("Development bonus recorded — it waits for approval in the list above.");
      setAmount("");
      setNote("");
      void qc.invalidateQueries({ queryKey: ["commissions"] });
    },
    onError: (e) => onError(errReason(e)),
  });

  const amountOk = /^\d{1,7}(\.\d{1,2})?$/.test(amount.trim()) && Number(amount) > 0;

  return (
    <Section
      title="Concierge development bonus"
      intro={
        <>
          Management sets the amount each quarter against the development criteria — it is never awarded automatically or at the maximum.
          {cap != null ? ` Yearly cap: ${money(cap)}.` : ""}
        </>
      }
    >
      <div className="grid gap-4 rounded-md border border-rule bg-white p-5 md:grid-cols-2 xl:grid-cols-[minmax(0,280px)_180px_180px_minmax(0,1fr)_auto] xl:items-end">
        <Field label="Concierge">
          <select className={INPUT} value={repId} onChange={(e) => setRepId(e.target.value)}>
            <option value="">Choose…</option>
            {conciergeReps.map((r) => (
              <option key={r.id} value={r.id}>
                {r.full_name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Quarter">
          <select className={INPUT} value={quarter} onChange={(e) => setQuarter(e.target.value)}>
            {quarters.map((qq) => (
              <option key={qq} value={qq}>
                {periodKeyLabel(qq)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Amount ($)">
          <input className={INPUT} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
        </Field>
        <Field label="Which criteria were met">
          <textarea className={TEXTAREA} rows={1} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Button onClick={() => create.mutate()} disabled={!repId || !amountOk || note.trim().length < 2 || create.isPending}>
          Record bonus
        </Button>
      </div>
    </Section>
  );
}
